import { isAxiosError } from 'axios';
import { combineEpics } from 'redux-observable';
import {
  defer,
  filter,
  forkJoin,
  from,
  map,
  Observable,
  of,
  retry,
  switchMap,
  takeUntil,
  timer,
  withLatestFrom,
} from 'rxjs';

import module from './index';

import photoModule from 'domains/photos';
import canvases from 'domains/canvases';
import activeEditor from 'domains/active_editor';

import { notNull } from 'utils/type_guards';
import { fetchPhotoBlob, loadBlobAsImage } from 'utils/image_load';
import { imageFilters } from 'utils/image_util';
import { catchAndLogError } from 'utils/common';

import type { Action } from '@reduxjs/toolkit';
import type { Epic } from 'redux-observable';
import type { RootState } from 'store/wos_store';
import type { CropEntity } from './crops.types';

const { actions, selectors, utils } = module;

type CropEpic = Epic<Action, Action, RootState>;

function fromBlobSrc(src: string) {
  return defer(() => fetchPhotoBlob(src)).pipe(
    retry({
      delay: (error, retries) => {
        // If the error is a 403, the preview image is not available yet
        if (isAxiosError(error) && error.response?.status === 403 && retries < 10) {
          return timer(2000 + 500 * retries);
        }

        // If the error was anything else, or the retry limit was hit,
        // we will catch this error in the epic and dispatch the necessary action
        throw error;
      },
    }),
    map((blob) => URL.createObjectURL(blob))
  );
}

// Init the active image node once we have nodes
const initializeActiveImageNode: CropEpic = (_, state$) =>
  state$.pipe(
    filter((state) => !selectors.activeImageNode(state)),
    map((state) => {
      const canvasId = canvases.selectors.activeId(state);
      const product = activeEditor.selectors.product(state);
      const cropsById = selectors.selectEntities(state);

      if (!canvasId || !product) return null;

      return utils.findNextAvailableCropId(cropsById, product, canvasId);
    }),
    filter(notNull),
    map((cropId) => actions.setActiveImageNode(utils.parseCropId(cropId).nodeUid))
  );

/**
 * Fetches a blob for an uploaded photo to be used in a crop
 */
export const fetchCropPhotoEpic: CropEpic = (action$, state$) =>
  action$.pipe(
    // Only continue when creating a crop
    filter(actions.createCrop.match),
    // switchmap to cancel previous
    switchMap(({ payload }) =>
      of(state$.value).pipe(
        // takeUntil to throw out the previous if we get a second createCrop action for the same canvas/node
        takeUntil(
          action$.pipe(
            filter((action) => {
              return (
                actions.createCrop.match(action) &&
                action.payload.canvasId === payload.canvasId &&
                action.payload.nodeUid === payload.nodeUid
              );
            })
          )
        ),
        map((state) => photoModule.selectors.selectById(state, payload.photoId)),
        filter(notNull),
        switchMap((photo) =>
          fromBlobSrc(photo.file_path_1024).pipe(
            map((blobSrc) => actions.fetchCropPhoto.fulfilled({ ...payload, blobSrc })),
            catchAndLogError(actions.fetchCropPhoto.rejected({ ...payload }))
          )
        )
      )
    )
  );

/**
 * Fetches a blob for a filtered image
 */
export const fetchCropFilterPhotoEpic: CropEpic = (action$, state$) =>
  action$.pipe(
    filter(actions.filterCrop.match),
    switchMap(({ payload }) =>
      of(state$.value).pipe(
        takeUntil(
          action$.pipe(
            filter(
              (action) =>
                actions.filterCrop.match(action) && action.payload.crop.id === payload.crop.id
            )
          )
        ),
        map((state) => photoModule.selectors.selectById(state, payload.crop.photoId)),
        filter(notNull),
        map((photo) => photo[payload.filter.filePathFull]),
        switchMap((src) =>
          fromBlobSrc(src).pipe(
            map((blobSrc) => actions.fetchCropFilterPhoto.fulfilled({ ...payload.crop, blobSrc })),
            catchAndLogError(actions.fetchCropFilterPhoto.rejected({ ...payload.crop }))
          )
        )
      )
    )
  );

/**
 * Rebuild the crops from a cart product, including loading the blob image
 */
export const restoreCartProductCropsEpic: CropEpic = (action$, state$) =>
  action$.pipe(
    filter(actions.restoreCartProductPhotoCrops.pending.match),
    withLatestFrom(state$),
    map(([{ payload }, state]) => {
      const photosById = photoModule.selectors.selectEntities(state);
      return payload
        .map((cpp) => {
          const photo = photosById[cpp.photoId];
          if (!photo) return null;

          const path = imageFilters.find((f) => f.filterType === cpp.filter);
          if (!path) return null;

          return { ...cpp, src: photo[path.filePathFull] };
        })
        .filter(notNull);
    }),
    switchMap((cpps) =>
      forkJoin(
        cpps.map(
          (cpp): Observable<CropEntity> =>
            from(loadBlobAsImage(cpp.src, cpp.crop.rotation)).pipe(
              map((image) => ({
                dimension: { height: image.naturalHeight, width: image.naturalWidth },
                src: image.src,
              })),
              map(({ dimension, src }) => ({
                ...utils.restoreCartProductPhotoCrop(cpp.crop, dimension),
                id: utils.buildCropId(cpp.canvasId, cpp.uid),
                canvasId: cpp.canvasId,
                nodeUid: cpp.uid,
                photoId: cpp.photoId,
                blobSrc: src,
                photoLoaded: true,
                filter: cpp.filter,
                originalH: dimension.height,
                originalW: dimension.width,
              }))
            )
        )
      )
    ),
    map(actions.restoreCartProductPhotoCrops.fulfilled)
  );

const cropEpics = combineEpics(
  initializeActiveImageNode,
  fetchCropPhotoEpic,
  fetchCropFilterPhotoEpic,
  restoreCartProductCropsEpic
);
export default cropEpics;
